import type { Consumption } from '@markku/types'
import { groupBy, sortBy } from '@markku/utils'
import { msg, type MacroMessageDescriptor } from '@lingui/core/macro'
import {
  IconBolt,
  IconDroplet,
  IconFlame,
  type Icon,
} from '@tabler/icons-react'
import { useMemo } from 'react'

export const CONSUMPTION_TYPES = [
  { value: 'electricity', label: msg`Electricity`, unit: 'kWh' },
  { value: 'water', label: msg`Water`, unit: 'm³' },
  { value: 'heating', label: msg`Heating`, unit: 'MWh' },
] as const

export const TYPE_CONFIG: Record<
  'electricity' | 'water' | 'heating',
  { label: MacroMessageDescriptor; icon: Icon; color: string; bgColor: string }
> = {
  electricity: {
    label: msg`Electricity`,
    icon: IconBolt,
    color: 'yellow.6',
    bgColor: 'yellow.0',
  },
  water: {
    label: msg`Water`,
    icon: IconDroplet,
    color: 'blue.6',
    bgColor: 'blue.0',
  },
  heating: {
    label: msg`Heating`,
    icon: IconFlame,
    color: 'orange.6',
    bgColor: 'orange.0',
  },
}

// Helper to enrich consumptions with calculated values in a single pass
export const enrichConsumptionsWithValues = (
  consumptions: Consumption[],
): Array<Consumption & { calculatedValue: number }> => {
  // Group by property + type
  const grouped = groupBy(consumptions, (c) => `${c.propertyId}-${c.type}`)

  const enriched: Array<Consumption & { calculatedValue: number }> = []

  // Process each group (sorted by date)
  for (const [, group] of grouped) {
    const sorted = sortBy(group, (c) => new Date(c.date).getTime())

    for (let i = 0; i < sorted.length; i++) {
      const current = sorted[i]
      const previous = i > 0 ? sorted[i - 1] : null

      const calculatedValue = previous
        ? Math.max(
            0,
            Number(current.meterReading) - Number(previous.meterReading),
          )
        : 0

      enriched.push({ ...current, calculatedValue })
    }
  }

  return enriched
}

interface ConsumptionCalculation {
  consumption: number
  previousReading: number
  previousDate: string | undefined
  isFirst: boolean
}

interface UseConsumptionCalculationProps {
  meterReading: number
  type: string
  allConsumptions: Consumption[]
  currentDate?: string
}

// Hook to calculate consumption based on current meter reading and all consumptions
export const useConsumptionCalculation = ({
  meterReading,
  type,
  allConsumptions,
  currentDate,
}: UseConsumptionCalculationProps): ConsumptionCalculation => {
  return useMemo(() => {
    // Filter to same type and optionally before current date (for edit mode)
    const typeConsumptions = allConsumptions.filter((c) => {
      if (c.type !== type) return false
      if (currentDate && c.date >= currentDate) return false
      return true
    })

    if (!typeConsumptions.length) {
      return {
        consumption: 0,
        previousReading: 0,
        previousDate: undefined,
        isFirst: true,
      }
    }

    // Sort by date descending to get the latest
    const sorted = sortBy(typeConsumptions, [
      (c) => new Date(c.date).getTime(),
      'desc',
    ])
    const latestReading = sorted[0]
    const previousReading = Number(latestReading.meterReading)

    // CRITICAL: First reading establishes baseline with 0 consumption
    // Only subsequent readings calculate actual consumption
    const consumption = Math.max(0, meterReading - previousReading)

    return {
      consumption,
      previousReading,
      previousDate: latestReading.date,
      isFirst: false,
    }
  }, [meterReading, type, allConsumptions, currentDate])
}
